import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { grantAccess } from "@/components/triarch/TriarchHeader";
import { HeroScrollCue } from "@/components/triarch/HeroScrollCue";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.08 + i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const scrollToSignal = () => {
    const el = document.getElementById("signal");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden pt-20"
    >
      {/* one-sided bloom */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(900px 560px at 50% 8%, rgba(215,220,227,0.12), rgba(7,8,10,0) 60%)",
        }}
      />
      {/* faint antique-gold trace */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(720px 480px at 50% 12%, rgba(196,164,97,0.08), rgba(7,8,10,0) 62%)",
        }}
      />
      {/* grid */}
      <div className="pointer-events-none absolute inset-0 z-0 triarch-grid opacity-40" />
      {/* vignette */}
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(1200px 760px at 50% 44%, rgba(7,8,10,0) 42%, rgba(7,8,10,0.82) 100%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-4xl px-5 text-center sm:px-8">
        <div className="flex flex-col items-center">
          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="show"
            data-testid="hero-headline"
            className="font-display text-4xl font-semibold leading-[1.06] tracking-[-0.02em] text-white text-glow sm:text-5xl lg:text-[4.4rem]"
          >
            Secure Data Infrastructure
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="show"
            data-testid="hero-subtext"
            className="text-motto mx-auto mt-7 max-w-2xl font-mono-accent text-[14px] font-medium uppercase leading-[1.8] tracking-[0.24em] sm:text-[15px]"
          >
            Infinita in ordinem, structura sub chaos.
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate="show"
            className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
          >
            <button
              data-testid="hero-request-access-button"
              onClick={grantAccess}
              className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-mono-accent text-xs uppercase tracking-[0.18em] text-[#07080A] transition-all duration-300 hover:shadow-[0_0_38px_rgba(203,174,120,0.32)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              Request Access
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={scrollToSignal}
              className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-transparent px-6 py-3 font-mono-accent text-xs uppercase tracking-[0.18em] text-white/70 transition-all duration-300 hover:border-white/30 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            >
              Enter Signal
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={4}
            initial="hidden"
            animate="show"
            className="mt-14 flex items-center justify-center font-mono-accent text-[10px] uppercase tracking-[0.22em] text-white/30"
          >
            <span>CLEARANCE: INTERNAL</span>
          </motion.div>
        </div>
      </div>

      <HeroScrollCue />
    </section>
  );
};
